import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Film, Camera, Music, Video, Monitor, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import OptimizedImage from '../components/OptimizedImage';

const MediaProduction = () => {
  const pastProjects = [
    {
      id: 1,
      title: "Luxury Automotive TVC Campaign",
      category: "TV Commercial",
      description: "Produced a high-end 60-second TV commercial for a luxury car brand. Shot on location in Bangkok and Phuket with celebrity endorsement, featuring cutting-edge cinematography and post-production.",
      client: "Premium Auto Brand",
      deliverable: "TV Commercial + Social Media Cuts",
      date: "March 2024"
    },
    {
      id: 2,
      title: "Corporate Brand Film",
      category: "Corporate Video",
      description: "Created a compelling 5-minute corporate documentary showcasing the company's journey, values, and vision. Included executive interviews, B-roll footage, and motion graphics.",
      client: "Fortune 500 Company",
      deliverable: "Corporate Documentary",
      date: "February 2024"
    },
    {
      id: 3,
      title: "Fashion Brand Web Series",
      category: "Web Series",
      description: "Developed and produced a 6-episode web series for luxury fashion brand. Featured influencer collaborations, behind-the-scenes content, and product showcases.",
      client: "International Fashion House",
      deliverable: "6-Episode Web Series",
      date: "January 2024"
    },
    {
      id: 4,
      title: "Music Video Production",
      category: "Music Video",
      description: "Full-scale music video production for chart-topping artist. Included concept development, choreography coordination, and visual effects.",
      client: "Leading Music Label",
      deliverable: "Official Music Video",
      date: "December 2023"
    },
    {
      id: 5,
      title: "Product Photography Campaign",
      category: "Photography",
      description: "High-end product photography for luxury watch collection. Studio and lifestyle shots with professional retouching for print and digital use.",
      client: "Swiss Watch Brand",
      deliverable: "150+ Product Images",
      date: "November 2023"
    },
    {
      id: 6,
      title: "Brand Jingle & Audio Production",
      category: "Audio Production",
      description: "Composed and produced original brand jingle and audio branding elements. Included multiple variations for different media platforms.",
      client: "Consumer Electronics Brand",
      deliverable: "Audio Brand Identity",
      date: "October 2023"
    }
  ];

  const services = [
    "TV Commercials & Digital Films",
    "Corporate Videos & Web Series",
    "Audio & Jingle Production",
    "Professional Scripting",
    "High-End Photoshoots",
    "Post-Production Excellence",
    "Motion Graphics & Animation",
    "Video Editing & Color Grading",
    "Sound Design & Mixing",
    "Drone & Aerial Cinematography",
    "Live Streaming Production",
    "Documentary Filmmaking"
  ];

  const productionServices = [
    {
      title: "Video Production",
      description: "Cinematic storytelling with state-of-the-art equipment and creative direction",
      icon: Video
    },
    {
      title: "Photography",
      description: "Professional commercial and event photography with expert retouching",
      icon: Camera
    },
    {
      title: "Audio Production",
      description: "Studio-quality audio recording, mixing, and original music composition",
      icon: Music
    },
    {
      title: "Post-Production",
      description: "Expert editing, color grading, VFX, and motion graphics",
      icon: Monitor
    }
  ];

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/m&P.jpg"
            alt="Media & Production"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">Premium Service</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            Media &
            <span className="gold-gradient-text block mt-2">Production</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            Creating compelling visual narratives that captivate audiences. From TV commercials to corporate films, we deliver world-class production services that elevate your brand.
          </p>
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg">
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Services */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Expertise</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Full-Spectrum
              <span className="gold-gradient-text"> Production</span>
            </h2>
            <p className="text-gray-300 text-lg">
              End-to-end media production services with creative excellence and technical precision.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {productionServices.map((service, index) => (
              <Card key={index} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                <div className="p-8 space-y-4">
                  <div className="bg-gold/10 w-16 h-16 rounded-sm flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <service.icon className="text-gold" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          <div className="max-w-6xl mx-auto">
            <h3 className="text-xl font-semibold text-silver mb-6">What We Offer:</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="flex items-start gap-3 group">
                  <CheckCircle2 className="text-gold flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" size={20} />
                  <span className="text-gray-300">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Past Projects */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Work</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Past
              <span className="gold-gradient-text"> Projects</span>
            </h2>
            <p className="text-gray-300 text-lg">
              A showcase of our production excellence across various media formats and industries.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pastProjects.map((project) => (
              <Card key={project.id} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                <div className="p-8 space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gold text-xs tracking-wider uppercase">{project.category}</span>
                      <span className="text-gray-400 text-xs">{project.date}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gold/10">
                    <div className="flex items-center gap-2 text-sm">
                      <Film className="text-gold" size={16} />
                      <span className="text-gray-400">Client:</span>
                      <span className="text-silver font-semibold">{project.client}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Monitor className="text-gold" size={16} />
                      <span className="text-gray-400">Deliverable:</span>
                      <span className="text-silver text-xs">{project.deliverable}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Ready to Create
              <span className="gold-gradient-text"> Compelling Content?</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Let's discuss your vision and bring it to life with world-class production services.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-6 text-lg rounded-none transition-all duration-300 flex items-center gap-2">
                  Start Your Project
                  <ArrowRight size={20} />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-6 text-lg rounded-none transition-all duration-300">
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MediaProduction;

